import { FC, useEffect, useRef, useState, WheelEvent } from "react";
import ContainerWithOverlay from "../ContainerWithOverlay/ContainerWithOverlay";
import "./HtmlPreview.css";

interface IHtmlPreviewProps {
    content: string;
}

/**
 * Превью html
 *
 * @param {String} content - Содержимое.
 * @returns {JSX.Element} - JSX-элемент, представляющий компонент превью.
 */
const HtmlPreview: FC<IHtmlPreviewProps> = ({ content }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [imgSrc, setImgSrc] = useState<string | undefined>();
    const [imageZoom, setImageZoom] = useState<number>(1);
    const fullscreenImageRef = useRef<HTMLImageElement>(null);

    const updateWidgetHeadersZIndex = (zIndex: string) => {
        const allWidgetHeaders = document.querySelectorAll('.widgets_header');
        allWidgetHeaders.forEach(header => {
            if (header instanceof HTMLElement) {
                header.style.zIndex = zIndex;
            }
        });
    };

    useEffect(() => {
        const handleClick = (event: MouseEvent) => {
            const img = (event.target as HTMLElement).closest(".html-preview img");
            
            if (img && containerRef.current?.contains(img)) {
                openFullScreen((img as HTMLImageElement).src);
                updateWidgetHeadersZIndex("1");
            } else {
                updateWidgetHeadersZIndex("50");
            }
        };

        const container = containerRef.current;
        if (container) {
            container.addEventListener("click", handleClick);
        }

        return () => {
            if (container) {
                container.removeEventListener("click", handleClick);
            }
        };
    }, []);

    const openFullScreen = (src: string) => {
        setImgSrc(src);
        setImageZoom(1);
    };

    useEffect(() => {
        //Делается через document потому что необходима конструкция passive: false
        //доступная только так. Отвязка ивента нужна, иначе не работает, особенности react
        const element: any = document.querySelector(".overlay_container");
        if (!element) return;
        element.addEventListener("wheel", handleImageZoom, {
            passive: false,
        });
        return () => element.removeEventListener("wheel", handleImageZoom);
    }, [imageZoom, imgSrc]);

    const handleImageZoom = (e: WheelEvent<HTMLDivElement>) => {
        if (e.ctrlKey) {
            e.preventDefault();
            e.stopPropagation();

            setImageZoom((z) => {
                const newZoom = e.deltaY < 0 ? z + 0.1 : z - 0.1;
                return Math.max(0.1, newZoom);
            });
        }
    };

    const parser = new DOMParser();
    const dom = parser.parseFromString(content, "text/html");

    return (
        <div className="html-preview" ref={containerRef}>
            {imgSrc ? (
                <ContainerWithOverlay
                    style={{ overflow: "scroll" }}
                    childrenStyle={
                        fullscreenImageRef.current
                            ? {
                                  position: "absolute",
                                  top: `clamp(0px, calc(50% - ${
                                      (fullscreenImageRef.current.clientHeight / 2) * imageZoom
                                  }px), 100%)`,
                                  left: `clamp(0px, calc(50% - ${
                                      (fullscreenImageRef.current.clientWidth / 2) * imageZoom
                                  }px), 100%)`,
                              }
                            : {}
                    }
                    onWheel={handleImageZoom}
                    onOverlayClick={() => setImgSrc(undefined)}
                >
                    <img
                        alt="attachment"
                        ref={fullscreenImageRef}
                        src={imgSrc}
                        className="fullscreen_image"
                        style={{ zoom: imageZoom }}
                    />
                </ContainerWithOverlay>
            ) : null}
            <div dangerouslySetInnerHTML={{ __html: dom.body.outerHTML }} />
        </div>
    );
};

export default HtmlPreview;
